import React, { useState, useEffect, useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Button } from 'primereact/button';
import Grid from "../../../components/components/Grid/Grid";
import { CompanyCommissionRateDto, createEmptyCommissionRate, EMPTY_GUID } from '../Model/CompanyCommissionRate';
import CarrierManagementAPI from '../Services/CarrierManagementAPI';
import CarrierCommissionService from './CarrierCommissionService';
import LookupService from '../../../Services/LookupService';
import { SelectOption } from '../../../shared/components/input';
import { useConfirmationModalContext } from '../../../shared/components/ModalConfirmationContext';
import AppContext from '../../../shared/components/AppContext';
import { Spinner } from '../../../components';
import { RootState } from '../../../shared/store';
import { updateLookupData } from '../../../customer/customer-slice/Customer-lookup-slice';
import * as constant from '../../../shared/util/constant';
import * as tblConsts from '../../../shared/util/tableConstant';
import './CarrierCommissionGrid.scss';

const AGENCY_DIRECT_OPTIONS: SelectOption[] = [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' }
];

const BILL_CODE_OPTIONS: SelectOption[] = [
    { label: 'Direct Bill', value: 'Direct Bill' },
    { label: 'Agency Bill', value: 'Agency Bill' }
];

/** Convert a value to number or null (avoids repeated null checks) */
const toNullableNumber = (val: any): number | null => {
    if (val === '' || val === null || val === undefined) return null;
    const num = Number(val);
    return isNaN(num) ? null : num;
};

interface CarrierCommissionGridProps {
    companyId?: string;
    isActive?: boolean;
    canCreate?: boolean;
    canUpdate?: boolean;
    canDelete?: boolean;
}

interface ICommissionGridState {
    commissionRates: any[];
    gridColumns: any[];
    isRowDataAddedOrEdited: boolean;
}

const commissionGridInitData: ICommissionGridState = {
    commissionRates: [],
    gridColumns: [],
    isRowDataAddedOrEdited: false
};

const CarrierCommissionGrid: React.FC<CarrierCommissionGridProps> = ({ companyId, isActive = true, canCreate = true, canUpdate = true, canDelete = true }) => {
    const appContext = useContext(AppContext);
    const modalContext = useConfirmationModalContext();
    const dispatch = useDispatch();

    // Use refs for service instances to avoid re-creation on every render
    const commissionServiceRef = useRef(new CarrierCommissionService());
    const apiRef = useRef(new CarrierManagementAPI());

    // Redux lookup selectors — reuse shared lookup data
    const reduxAgencyList = useSelector((state: RootState) => state?.LookupState.agencyList);
    const reduxPolicyTypeList = useSelector((state: RootState) => state?.LookupState.policyTypeList);
    const reduxStateUSList = useSelector((state: RootState) => state?.LookupState.stateUSList);

    const [gridState, setGridState] = useState<ICommissionGridState>(commissionGridInitData);
    const [navigateToFirstPage, setNavigateToFirstPage] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [lookupsLoaded, setLookupsLoaded] = useState(false);

    // Refs to avoid stale closure in fetchCommissionRates
    const lookupsRef = useRef<{ agencies: any[]; policyTypes: any[]; marketTypes: any[]; businessTypes: any[]; planTypes: any[]; groupTypes: any[]; writingCompanies: any[]; usStates: any[] }>({
        agencies: [], policyTypes: [], marketTypes: [], businessTypes: [], planTypes: [], groupTypes: [], writingCompanies: [], usStates: []
    });

    const { commissionRates, gridColumns } = gridState;

    // Track unsaved changes for page-leave warning
    useEffect(() => {
        appContext.formSavedStatus = !gridState.isRowDataAddedOrEdited;
    }, [gridState.isRowDataAddedOrEdited]);

    // Browser refresh/close warning
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (gridState.isRowDataAddedOrEdited) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [gridState.isRowDataAddedOrEdited]);

    // Reset grid when navigating away with unsaved changes (user confirmed leave)
    useEffect(() => {
        if (!isActive && gridState.isRowDataAddedOrEdited) {
            // Remove new rows and cancel editing on existing rows
            setGridState(prev => {
                const resetRates = prev.commissionRates
                    .filter(r => r.id && r.id !== EMPTY_GUID) // remove unsaved new rows
                    .map(r => ({ ...r, enableGridRowEditing: false, isGridInEditMode: false, editingRow: undefined }));
                return { ...prev, commissionRates: resetRates, isRowDataAddedOrEdited: false };
            });
        }
    }, [isActive]);

    // Fetch lookups on mount — use Redux for agencies/policyTypes/usStates if already populated
    useEffect(() => {
        const fetchLookups = async () => {
            try {
                const commissionService = commissionServiceRef.current;
                const api = apiRef.current;

                // Only fetch from API if not already available in Redux
                const [agencyData, policyTypeData, usStateData] = await Promise.all([
                    reduxAgencyList.length > 0
                        ? Promise.resolve(null)
                        : LookupService.fetchAgencies(),
                    reduxPolicyTypeList.length > 0
                        ? Promise.resolve(null)
                        : LookupService.fetchPolicyType(),
                    reduxStateUSList.length > 0
                        ? Promise.resolve(null)
                        : LookupService.fetchUSStates(),
                ]);

                // Fetch lookups not available in Redux
                const [marketTypeData, businessTypeData, planTypeData, groupTypeData, carriersData] = await Promise.all([
                    LookupService.getLookupData(tblConsts.MARKET_TYPE_TABLE_ID),
                    LookupService.getLookupData(tblConsts.BUSINESS_TYPE_TABLE_ID),
                    LookupService.getLookupData(tblConsts.PLAN_TYPE_TABLE_ID),
                    LookupService.getLookupData(tblConsts.GROUP_TYPE_TABLE_ID),
                    api.getCarriers()
                ]);

                // Push to Redux if fetched fresh (so other components benefit)
                if (agencyData) {
                    const agencyOpts = agencyData.map((a: any) => ({ label: a.lookupValue, value: a.id }));
                    dispatch(updateLookupData({ data: agencyOpts, type: constant.SlicerLookUpConstant.AGENCY }));
                }
                if (policyTypeData) {
                    const policyOpts = policyTypeData.map((p: any) => ({ label: p.lookupValue, value: p.id }));
                    dispatch(updateLookupData({ data: policyOpts, type: constant.SlicerLookUpConstant.POLICY_TYPE }));
                }
                if (usStateData) {
                    const stateOpts = usStateData.map((s: any) => ({ label: s.lookupCode, value: s.lookupCode }));
                    dispatch(updateLookupData({ data: stateOpts, type: constant.SlicerLookUpConstant.US_STATE }));
                }

                // Resolve final lookup arrays (prefer freshly fetched, fallback to Redux)
                // Spread Redux arrays to create mutable copies (Redux/Immer freezes state)
                const finalAgencyOptions: SelectOption[] = agencyData
                    ? agencyData.map((a: any) => ({ label: a.lookupValue, value: a.id }))
                    : [...reduxAgencyList];
                const finalPolicyTypeOptions: SelectOption[] = policyTypeData
                    ? policyTypeData.map((p: any) => ({ label: p.lookupValue, value: p.id }))
                    : [...reduxPolicyTypeList];
                const finalStateOptions: SelectOption[] = usStateData
                    ? usStateData.map((s: any) => ({ label: s.lookupCode, value: s.lookupCode }))
                    : [...reduxStateUSList];
                const marketTypeOpts = marketTypeData.map((m: any) => ({ label: m.lookupValue, value: m.id }));
                const businessTypeOpts = businessTypeData.map((b: any) => ({ label: b.lookupValue, value: b.id }));
                const planTypeOpts = planTypeData.map((p: any) => ({ label: p.lookupValue, value: p.id }));
                const groupTypeOpts = groupTypeData.map((g: any) => ({ label: g.lookupValue, value: g.id }));
                const writingCompanyOpts: SelectOption[] = (carriersData || []).map((c: any) => ({ label: c.name, value: c.id }));

                // Keep ref in sync for fetchCommissionRates (enrichDisplayFields uses label-based lookup)
                lookupsRef.current = {
                    agencies: agencyData || finalAgencyOptions,
                    policyTypes: policyTypeData || finalPolicyTypeOptions,
                    marketTypes: marketTypeData,
                    businessTypes: businessTypeData,
                    planTypes: planTypeData,
                    groupTypes: groupTypeData,
                    writingCompanies: carriersData || [],
                    usStates: usStateData || finalStateOptions
                };

                // Build grid columns with loaded lookup data
                const cols = commissionService.getGridColumns(
                    onEditingCellDataChange,
                    gridCellClickHandler,
                    finalAgencyOptions,
                    finalStateOptions,
                    finalPolicyTypeOptions,
                    marketTypeOpts,
                    businessTypeOpts,
                    planTypeOpts,
                    groupTypeOpts,
                    writingCompanyOpts,
                    AGENCY_DIRECT_OPTIONS,
                    BILL_CODE_OPTIONS,
                    !canUpdate,
                    !canDelete
                );
                setGridState(prev => ({ ...prev, gridColumns: cols }));
                setLookupsLoaded(true);
            } catch (ex) {
                toast.error('Error loading lookup data.');
            }
        };
        fetchLookups();
    }, []);

    // Fetch commission rates when tab becomes active and lookups are loaded
    useEffect(() => {
        if (!companyId || !isActive || hasFetched || !lookupsLoaded) return;
        fetchCommissionRates();
    }, [companyId, isActive, lookupsLoaded]);

    // Show loading as soon as tab is active and data not yet fetched
    useEffect(() => {
        if (isActive && !hasFetched && companyId) {
            setLoading(true);
        }
    }, [isActive, companyId]);

    // Reset fetch flag when companyId changes
    useEffect(() => {
        setHasFetched(false);
    }, [companyId]);

    const fetchCommissionRates = async () => {
        if (!companyId) return;
        setLoading(true);
        try {
            const api = apiRef.current;
            const commissionService = commissionServiceRef.current;
            const data = await api.getCommissionRates(companyId);
            const lk = lookupsRef.current;
            const enrichedData = data.map((row: any) => {
                const enriched: any = { ...row };
                enriched.isGridInEditMode = false;
                enriched.enableGridRowEditing = false;
                commissionService.enrichDisplayFields(enriched, lk.agencies, lk.policyTypes, lk.marketTypes, lk.businessTypes, lk.planTypes, lk.groupTypes, lk.writingCompanies, lk.usStates);
                return enriched;
            });
            setGridState(prev => ({ ...prev, commissionRates: enrichedData, isRowDataAddedOrEdited: false }));
            setHasFetched(true);
        } catch (ex) {
            toast.error('Error loading commission rates.');
        }
        setLoading(false);
    };

    const onEditingCellDataChange = (editingRow: any, column: any, value: any, _additionalInfo: any) => {
        editingRow[column.fieldName] = value;
    };

    const gridCellClickHandler = (originalRow: any, _column: any, additionalData: any) => {
        switch (additionalData) {
            case 'save_row':
                saveRowClickHandler(originalRow);
                break;
            case 'cancel_row_editing':
                cancelRowEditingClickHandler(originalRow);
                break;
            case 'edit_row':
                if (canUpdate) editRowClickHandler(originalRow);
                break;
            case 'delete_row':
                if (canDelete) deleteRowClickHandler(originalRow);
                break;
            default:
                break;
        }
    };

    const editRowClickHandler = (originalRow: any) => {
        originalRow.editingRow = { ...originalRow };
        originalRow.enableGridRowEditing = true;

        setGridState(prev => {
            prev.commissionRates.forEach(r => { r.isGridInEditMode = true; });
            return { ...prev, commissionRates: [...prev.commissionRates], isRowDataAddedOrEdited: true };
        });
    };

    const saveRowClickHandler = async (originalRow: any) => {
        try {
            const api = apiRef.current;
            const commissionService = commissionServiceRef.current;
            let editingRow = originalRow.editingRow;
            if (!editingRow) return;

            let validationErrors = commissionService.validateRow(originalRow);
            editingRow.errors = validationErrors;

            if (Object.keys(validationErrors).length > 0) {
                toast.warn('Please fix validation errors before saving.');
                setGridState(prev => ({ ...prev, commissionRates: [...prev.commissionRates] }));
                return;
            }

            // Build DTO from editingRow
            const dto: CompanyCommissionRateDto = {
                id: editingRow.id || EMPTY_GUID,
                companyId: editingRow.companyId,
                agencyId: editingRow.agencyId,
                stateCode: editingRow.stateCode,
                policyType: editingRow.policyType || null,
                agencyDirect: editingRow.agencyDirect || null,
                billCode: editingRow.billCode || null,
                minAge: toNullableNumber(editingRow.minAge),
                maxAge: toNullableNumber(editingRow.maxAge),
                geoTier: toNullableNumber(editingRow.geoTier),
                marketType: editingRow.marketType || null,
                businessType: editingRow.businessType || null,
                planType: editingRow.planType || null,
                groupType: editingRow.groupType || null,
                writingCompanyId: editingRow.writingCompanyId || null,
                rate: toNullableNumber(editingRow.rate),
                effectiveDate: editingRow.effectiveDate instanceof Date ? editingRow.effectiveDate : (editingRow.effectiveDate ? new Date(editingRow.effectiveDate) : null),
                county: editingRow.county || null,
                zipCode: editingRow.zipCode || null,
                notes: editingRow.notes || null
            };

            if (!dto.id || dto.id === EMPTY_GUID) {
                await api.createCommissionRate(dto);
                toast.success('Commission rate created successfully.');
            } else {
                await api.updateCommissionRate(dto);
                toast.success('Commission rate updated successfully.');
            }

            // Refresh data from server
            await fetchCommissionRates();
        } catch (ex: any) {
            const errorMsg = ex?.data?.message || ex?.message || 'Error saving commission rate.';
            toast.error(errorMsg);
        }
    };

    const cancelRowEditingClickHandler = async (originalRow: any) => {
        const result = await modalContext.showConfirmation('Are you sure you want to discard changes?');
        if (!result) return;

        setGridState(prev => {
            prev.commissionRates.forEach(r => { r.isGridInEditMode = false; });
            let rates: any[];
            if (originalRow.id && originalRow.id !== EMPTY_GUID) {
                delete originalRow.editingRow;
                originalRow.enableGridRowEditing = false;
                rates = [...prev.commissionRates];
            } else {
                // Remove new unsaved row
                rates = prev.commissionRates.filter(r => r.id !== originalRow.id);
            }
            return { ...prev, commissionRates: rates, isRowDataAddedOrEdited: false };
        });
    };

    const deleteRowClickHandler = async (originalRow: any) => {
        const result = await modalContext.showConfirmation('Are you sure you want to delete this commission rate?');
        if (!result) return;

        try {
            if (originalRow.id && originalRow.id !== EMPTY_GUID) {
                await apiRef.current.deleteCommissionRate(originalRow.id);
                toast.success('Commission rate deleted successfully.');
                await fetchCommissionRates();
            } else {
                // Remove unsaved new row locally
                setGridState(prev => {
                    prev.commissionRates.forEach(r => { r.isGridInEditMode = false; });
                    const rates = prev.commissionRates.filter(r => r.id !== originalRow.id);
                    return { ...prev, commissionRates: rates, isRowDataAddedOrEdited: false };
                });
            }
        } catch (ex: any) {
            const errorMsg = ex?.data?.message || ex?.message || 'Error deleting commission rate.';
            toast.error(errorMsg);
        }
    };

    const onAddRowClick = () => {
        if (!companyId) return;
        setGridState(prev => {
            const commissionService = commissionServiceRef.current;
            prev.commissionRates.forEach(r => { r.isGridInEditMode = true; });
            let newRow = commissionService.getNewRow(companyId);
            let newList = [newRow, ...prev.commissionRates];
            return { ...prev, commissionRates: newList, isRowDataAddedOrEdited: true };
        });
        setNavigateToFirstPage(true);
    };

    return (
        <div className="carrier-commission-grid-container p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-semibold mb-0">Commission Rates</h5>
                <Button
                    label="Add Row"
                    icon="bi bi-plus-circle"
                    onClick={onAddRowClick}
                    disabled={gridState.isRowDataAddedOrEdited || !canCreate}
                    className="p-button-sm"
                />
            </div>
            {loading && <Spinner loading={true} type={"SyncLoader"} />}
            {!loading && (
            <div className="commission-grid-div">
                {gridColumns.length > 0 && (
                    <Grid
                        data={commissionRates}
                        showFilters={false}
                        id={'carrierCommissionGrid'}
                        retainGridState={true}
                        columns={gridColumns}
                        hiddenColumns={['selection']}
                        sortDefault={[]}
                        paginationDisabled={true}
                        grdTabelClass="table-striped table-sm"
                        grdTableContainerClass="commission-grid-table-container"
                        zindexHeader={10000}
                        navigateToFirstPage={navigateToFirstPage}
                        onNavigateToFirstPage={() => setNavigateToFirstPage(false)}
                    />
                )}
            </div>
            )}
        </div>
    );
};

export default CarrierCommissionGrid;
